import { CommonModule } from '@angular/common';
import { Component, Input, inject, signal, computed, AfterViewInit, OnInit, DestroyRef } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule } from '@ngx-translate/core';
import { Environment, EnvironmentService } from '@galaxy/core';
import { BrandingV2Interface, BrandingV2Service } from '@galaxy/partner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { WebchatInstallInstructionsDialogComponent } from './webchat-install-instructions-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { CodeSampleComponent } from '@vendasta/galaxy/code-sample';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { COMPANY_NAME_TOKEN, PARTNER_ID_TOKEN } from '../../../../../core/src/index';
import { switchMap, map, catchError, of } from 'rxjs';
import slugify from 'slugify';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const fileNamePrefix = 'inbox-pro-web-chat-widget';

@Component({
  selector: 'inbox-web-chat-instructions',
  styleUrls: ['./web-chat-instructions.component.scss'],
  imports: [
    CommonModule,
    MatCardModule,
    MatDialogModule,
    MatTabsModule,
    TranslateModule,
    MatButtonModule,
    CodeSampleComponent,
  ],
  templateUrl: './web-chat-instructions.component.html',
})
export class WebChatInstructionsComponent implements AfterViewInit, OnInit {
  private readonly environmentService = inject(EnvironmentService);
  private readonly partnerService = inject(BrandingV2Service);
  private readonly dialog = inject(MatDialog);
  private readonly httpClient = inject(HttpClient);
  private readonly companyName$ = inject(COMPANY_NAME_TOKEN);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly isNativePlatform = Capacitor.isNativePlatform();
  private readonly _widgetId = signal<string>('');
  private readonly _widgetNamespace = signal<string>('');
  private readonly destroyRef = inject(DestroyRef);
  private fileName = signal<string>(fileNamePrefix + '.zip');

  @Input() set widgetId(value: string) {
    this._widgetId.set(value);
  }

  @Input() set widgetNamespace(value: string) {
    this._widgetNamespace.set(value);
  }

  constructor(private snackbarService: SnackbarService) {}

  ngOnInit() {
    slugify.extend({ "'": '' });
    if (this._widgetNamespace().startsWith('AG-')) {
      this.companyName$
        .pipe(
          catchError(() => of('')),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((companyName) => {
          if (companyName === '') {
            this.fileName.set(`${fileNamePrefix}-${this._widgetNamespace()}}.zip`);
          } else {
            this.fileName.set(`${fileNamePrefix}-${this._widgetNamespace()}-${slugify(companyName)}.zip`);
          }
        });
    } else {
      this.partnerId$
        .pipe(
          switchMap((partnerId: string) => this.partnerService.getBranding(partnerId)),
          catchError(() => of({})),
          map((branding: BrandingV2Interface) => branding.name ?? ''),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((brandName) => {
          if (brandName === '') {
            this.fileName.set(`${fileNamePrefix}-${this._widgetNamespace()}.zip`);
          } else {
            this.fileName.set(`${fileNamePrefix}-${this._widgetNamespace()}-${slugify(brandName)}.zip`);
          }
        });
    }
  }

  readonly codeSnippet = computed(() => {
    return `<script
  src="${this.getWidgetUrl()}"
  data-widget-id="${this._widgetId()}"
  defer
></script>`;
  });

  readonly gtmCodeSnippet = computed(() => {
    return `<script>
  var script = document.createElement("script");
  script.defer = true;
  script.async = true;
  script.src = "${this.getWidgetUrl()}";
  script.dataset.widgetId = "${this._widgetId()}";
  document.getElementsByTagName("head")[0].appendChild(script);
</script>`;
  });

  readonly godaddySnippet = computed(() => {
    return `<script>
  (function() {
    var script = document.createElement('script');
    script.src = "${this.getWidgetUrl()}";
    script.setAttribute("data-widget-id", "${this._widgetId()}");
    script.defer = true;

    if (window.top !== window.self) {
      window.top.document.body.appendChild(script);
    } else {
      document.body.appendChild(script);
    }
  })();
</script>`;
  });

  openInstallDetails(): void {
    this.dialog.open(WebchatInstallInstructionsDialogComponent, { width: 'auto' });
  }

  downloadPlugin(): void {
    const url = `https://conversation-${this.environmentService.getEnvironmentString()}.apigateway.co/widgets/${this._widgetId()}/plugins/wordpress`;
    this.httpClient
      .get(url, {
        observe: 'response',
        responseType: 'blob',
        withCredentials: true,
      })
      .subscribe((res) => {
        if (this.isNativePlatform) {
          this.handleMobileFileDownload(res);
        } else {
          this.handleFileDownload(res);
        }
      });
  }

  private getWidgetUrl(): string {
    const env =
      this.environmentService.getEnvironment() === Environment.DEMO ? 'webchat-client' : 'webchat-client..prod';
    return `https://cdn.apigateway.co/${env}/sdk.js`;
  }

  private handleFileDownload(response: HttpResponse<Blob>): void {
    if (!response.body) {
      return;
    }

    const blob = new Blob([response.body], { type: 'application/zip' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = this.fileName() || fileNamePrefix + '.zip';
    link.click();
  }

  private async handleMobileFileDownload(response: HttpResponse<Blob>): Promise<void> {
    if (!response.body) {
      return;
    }

    const blob = new Blob([response.body], { type: 'application/zip' });
    const base64 = (await this.convertBlobToBase64(blob)) as string;
    await Filesystem.writeFile({
      path: this.fileName() || fileNamePrefix + '.zip',
      data: base64,
      directory: Directory.Documents,
    })
      .then((res) =>
        this.snackbarService.openSuccessSnack('INBOX.WEBCHAT.SETTINGS.SUCCESS_WORD_PRESS_DOWNLOAD', {
          interpolateTranslateParams: { directory: res.uri },
        }),
      )
      .catch((_) => this.snackbarService.openErrorSnack('INBOX.WEBCHAT.SETTINGS.ERROR_WORD_PRESS_DOWNLOAD'));
  }
  private convertBlobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  ngAfterViewInit() {
    this.setTabHeights();
  }

  private setTabHeights() {
    const tabCardBody = document.querySelectorAll('mat-tab-body');
    if (!tabCardBody) return;

    const maxHeight = Math.max(...Array.from(tabCardBody).map((element: any) => element.clientHeight));

    tabCardBody.forEach((elm) => {
      elm.setAttribute('style', `height:${maxHeight}px;`);
    });
  }
}
